import React from "react";
import { Rocket, TrendingUp, Building2, Globe2 } from "lucide-react";

const LIFESTYLE_STARTUPS =
    "https://images.pexels.com/photos/7414216/pexels-photo-7414216.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LIFESTYLE_SCALEUPS =
    "https://images.pexels.com/photos/7413915/pexels-photo-7413915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function WhoWeServe() {
    return (
        <section
            id="who-we-serve"
            data-testid="who-we-serve-section"
            className="relative py-24 md:py-32 bg-white"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-14">
                    <span className="eyebrow text-bpo-cyanDark">Who we serve</span>
                    <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-bpo-ink leading-[1.05]">
                        Built for ambitious teams between
                        <span className="text-bpo-cyan"> idea </span>
                        and
                        <span className="text-bpo-cyan"> scale</span>.
                    </h2>
                    <p className="mt-5 text-base md:text-lg text-bpo-charcoal/80">
                        Accessible pricing, senior delivery and remote-first
                        engagement — so growth-stage companies can access the
                        same rigour as the enterprises they compete with.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
                    {/* Startups */}
                    <article
                        data-testid="serve-card-startups"
                        className="md:col-span-7 lg:col-span-7 rounded-3xl overflow-hidden border border-gray-200 bg-bpo-ink text-white relative min-h-[340px] flex flex-col"
                    >
                        <img
                            src={LIFESTYLE_STARTUPS}
                            alt="Startup founders collaborating"
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                        <div className="relative p-8 md:p-10 flex flex-col h-full justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-xl bg-bpo-cyan text-bpo-ink flex items-center justify-center">
                                    <Rocket className="h-5 w-5" />
                                </div>
                                <span className="eyebrow text-bpo-cyanLight">
                                    Startups
                                </span>
                            </div>
                            <div>
                                <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                                    From idea to repeatable revenue.
                                </h3>
                                <p className="mt-4 text-white/80 max-w-xl text-[15px] md:text-base leading-relaxed">
                                    Lean diagnostics, operating model design and
                                    early-stage CX so your first customers
                                    become your strongest advocates.
                                </p>
                            </div>
                        </div>
                    </article>

                    {/* Scaleups */}
                    <article
                        data-testid="serve-card-scaleups"
                        className="md:col-span-5 lg:col-span-5 rounded-3xl overflow-hidden border border-gray-200 bg-white relative min-h-[340px] flex flex-col"
                    >
                        <img
                            src={LIFESTYLE_SCALEUPS}
                            alt="Consultant presenting to a team"
                            className="absolute inset-0 w-full h-full object-cover opacity-90"
                        />
                        <div className="relative p-8 md:p-10 flex flex-col justify-between h-full bg-gradient-to-t from-white via-white/90 to-white/30">
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-xl bg-bpo-ink text-white flex items-center justify-center">
                                    <TrendingUp className="h-5 w-5" />
                                </div>
                                <span className="eyebrow text-bpo-charcoal">
                                    Scaleups
                                </span>
                            </div>
                            <div>
                                <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-bpo-ink leading-tight">
                                    Stabilise. Industrialise. Compound.
                                </h3>
                                <p className="mt-3 text-bpo-charcoal/80 text-[15px] leading-relaxed">
                                    Six Sigma rigour, change adoption and
                                    governance to scale without breaking what
                                    made you great.
                                </p>
                            </div>
                        </div>
                    </article>

                    {/* SMBs */}
                    <article
                        data-testid="serve-card-smb"
                        className="md:col-span-5 lg:col-span-5 rounded-3xl border border-gray-200 bg-bpo-cyanLight p-8 md:p-10 min-h-[260px] flex flex-col justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-xl bg-white border border-bpo-cyan/30 text-bpo-cyanDark flex items-center justify-center">
                                <Building2 className="h-5 w-5" />
                            </div>
                            <span className="eyebrow text-bpo-charcoal">
                                Small & Mid-sized
                            </span>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-bpo-ink leading-tight">
                                Enterprise-grade systems, SMB economics.
                            </h3>
                            <p className="mt-3 text-bpo-charcoal/85 text-[15px] leading-relaxed">
                                Modular engagements priced for owner-led
                                businesses — proven frameworks without the
                                consulting overhead.
                            </p>
                        </div>
                    </article>

                    {/* Inclusive growth */}
                    <article
                        data-testid="serve-card-inclusive"
                        className="md:col-span-7 lg:col-span-7 rounded-3xl border border-gray-200 bg-white p-8 md:p-10 min-h-[260px] flex flex-col justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <div className="h-11 w-11 rounded-xl bg-bpo-ink text-white flex items-center justify-center">
                                <Globe2 className="h-5 w-5" />
                            </div>
                            <span className="eyebrow text-bpo-charcoal">
                                Inclusive growth
                            </span>
                        </div>
                        <div>
                            <h3 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-bpo-ink leading-tight">
                                Purpose-aligned. Remote-first.
                                <span className="text-bpo-cyan">
                                    {" "}
                                    EU-wide.
                                </span>
                            </h3>
                            <p className="mt-3 text-bpo-charcoal/80 text-[15px] leading-relaxed max-w-2xl">
                                Registered in Sofia, delivered everywhere. We
                                work in inclusive teams across time zones,
                                cultures and languages — because sustainable
                                growth is everyone’s business.
                            </p>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    );
}
